// E-transfer notices (migration 0116, 2026-10-05). The Gmail that receives the
// Interac e-Transfer notices is read once a minute (netlify/functions/
// etransfer-sync-cron.js) into etransfer_notices; the view
// etransfer_notices_board links each notice to its Order Board card by the
// order number in the customer's message and says whether the customer
// followed the instructions. This service is the console's side:
// - the Payments page list (fetchNotices / subscribeNotices) and the manual
//   link / unlink (RPCs, recorded);
// - the board: subscribeBoardNotices / noticesByCard → the "e-Transfer sent"
//   chip and the Approve dialog's pre-filled reference (every dispatcher, with
//   or without Payments access — a limited feed, etransfer_board_notices());
// - access (client decision, 2026-10-06): Payments is the super admin's plus
//   the users the super admin grants in the Admin Panel (hasPaymentsAccess,
//   fetch/grant/revokePaymentsAccess);
// - the payments log (payment_events): fetchNoticeEvents / fetchAccessEvents.
// Autodeposit is off: a notice means the money was SENT. The dispatcher still
// accepts it in the bank, then approves the card as before. No email body is
// stored (it holds the deposit link): the panel shows the parsed values and
// opens the email itself in Gmail (gmailLink).

import { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { subscribeTableHub } from './tableHub';
import { orderKeyOf } from './paymentBoard';
import { UserRole, type User } from '../types';
import { isSuperTier } from './roles';

export type NoticeKind = 'sent' | 'reminder' | 'cancelled' | 'deposited' | 'expired' | 'other_interac' | 'not_payment';
export type NoticeFlag =
  | 'no_order_number' | 'extra_text' | 'unverified' | 'wrong_recipient_name'
  | 'order_not_found' | 'amount_mismatch' | 'duplicate' | 'cancelled';
export type NoticeStatus = 'not_payment' | 'info' | 'cancelled' | 'needs_link' | 'waiting' | 'accepted' | 'card_paid';

export interface PaymentNotice {
  id: number;
  messageId: string;
  /** The inbox it was read from (opens the right Gmail account). */
  mailbox: string;
  receivedAt: number;
  fromAddr: string;
  fromName: string;
  subject: string;
  kind: NoticeKind;
  verified: boolean;
  senderName: string | null;
  /** The recipient name the customer typed (must be the one in the instructions). */
  recipientName: string | null;
  amount: number | null;
  memo: string | null;
  interacRef: string | null;
  orderNumber: string | null;
  manualCardId: string | null;
  unlinked: boolean;
  linkedByName: string | null;
  linkedAt: number | null;
  cardId: string | null;
  cardOrderNumber: string | null;
  cardCustomerName: string | null;
  cardPhone: string | null;
  cardTotal: number | null;
  cardStage: string | null;
  flags: NoticeFlag[];
  status: NoticeStatus;
  /** Put aside by dispatch (never to be linked): off every list and the board until unarchived. */
  archivedAt: number | null;
  archivedByName: string | null;
}

export const FLAG_TEXT: Record<NoticeFlag, string> = {
  no_order_number: 'No order number',
  extra_text: 'Extra text in message',
  unverified: 'Not verified as Interac',
  wrong_recipient_name: 'Wrong recipient name',
  order_not_found: 'Order not found',
  amount_mismatch: 'Amount differs from order',
  duplicate: 'Another transfer on this order',
  cancelled: 'Cancelled by sender',
};

export const STATUS_TEXT: Record<NoticeStatus, string> = {
  not_payment: 'Not a payment notice',
  info: 'Interac notice',
  cancelled: 'Cancelled',
  needs_link: 'Needs linking',
  waiting: 'Waiting for acceptance',
  accepted: 'Accepted',
  card_paid: 'Order paid',
};

/** The recipient name customers are told to enter (emailTemplates.ts / smsTemplates.ts). */
export const EXPECTED_RECIPIENT = 'Hellohi';

/** Flags that mean "do not accept": the notice may not be real money, or the customer must send again. */
const SERIOUS: NoticeFlag[] = ['unverified', 'cancelled', 'wrong_recipient_name'];

/** Green = instructions followed, amber = something to check, red = do not accept. */
export function noticeTone(flags: NoticeFlag[]): 'ok' | 'warn' | 'bad' {
  if (flags.some((f) => SERIOUS.includes(f))) return 'bad';
  return flags.length ? 'warn' : 'ok';
}

const ms = (v: string | null | undefined): number | null => (v ? Date.parse(v) : null);
const num = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v));

export function noticeFromRow(r: any): PaymentNotice {
  return {
    id: Number(r.id),
    messageId: r.message_id || '',
    mailbox: r.mailbox || '',
    receivedAt: Date.parse(r.received_at),
    fromAddr: r.from_addr || '',
    fromName: r.from_name || '',
    subject: r.subject || '',
    kind: r.kind,
    verified: !!r.verified,
    senderName: r.sender_name ?? null,
    recipientName: r.recipient_name ?? null,
    amount: num(r.amount),
    memo: r.memo ?? null,
    interacRef: r.interac_ref ?? null,
    orderNumber: r.order_number ?? null,
    manualCardId: r.manual_card_id ?? null,
    unlinked: !!r.unlinked,
    linkedByName: r.linked_by_name ?? null,
    linkedAt: ms(r.linked_at),
    cardId: r.card_id ?? null,
    cardOrderNumber: r.card_order_number ?? null,
    cardCustomerName: r.card_customer_name ?? null,
    cardPhone: r.card_phone ?? null,
    cardTotal: num(r.card_total),
    cardStage: r.card_stage ?? null,
    flags: (r.flags || []) as NoticeFlag[],
    status: r.status,
    archivedAt: ms(r.archived_at),
    archivedByName: r.archived_by_name ?? null,
  };
}

const LIST_COLUMNS =
  'id,message_id,mailbox,received_at,from_addr,from_name,subject,kind,verified,sender_name,recipient_name,amount,memo,' +
  'interac_ref,order_number,manual_card_id,unlinked,linked_by_name,linked_at,card_id,card_order_number,card_customer_name,' +
  'card_phone,card_total,card_stage,flags,status,archived_at,archived_by_name';
const LIST_LIMIT = 500;

/** The newest notices (any kind), newest first. */
export async function fetchNotices(): Promise<PaymentNotice[]> {
  const { data, error } = await supabase()
    .from('etransfer_notices_board')
    .select(LIST_COLUMNS)
    .order('received_at', { ascending: false })
    .limit(LIST_LIMIT);
  if (error) throw error;
  return (data || []).map(noticeFromRow);
}

/**
 * The email itself, in Gmail (it is not kept here: its body holds the deposit link). By Message-ID the
 * search finds exactly that email; a synthetic id (`uid-N`, no Message-ID header) falls back to the
 * subject. `authuser` picks the e-transfer account when the browser is signed in to several.
 */
export function gmailLink(n: Pick<PaymentNotice, 'messageId' | 'mailbox' | 'subject'>): string {
  const id = n.messageId.replace(/^<|>$/g, '');
  const query = id && !/^uid-\d+$/.test(id) ? `rfc822msgid:${id}` : `subject:"${n.subject}"`;
  const user = n.mailbox ? `?authuser=${encodeURIComponent(n.mailbox)}` : '';
  return `https://mail.google.com/mail/u/${user}#search/${encodeURIComponent(query)}`;
}

const REFRESH_MS = 60_000; // card stages and totals change the view too; a minute is fresh enough

/** Live list: re-read on every notice change (new mail, link, unlink) and once a minute. */
export function subscribeNotices(onRows: (rows: PaymentNotice[]) => void, onError?: (e: Error) => void): () => void {
  let dead = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const load = () => {
    fetchNotices().then((rows) => { if (!dead) onRows(rows); }, (e) => { if (!dead) onError?.(e); });
  };
  const soon = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(load, 300);
  };
  load();
  const poll = setInterval(load, REFRESH_MS);
  const unsub = subscribeTableHub('etransfer_notices', { onChange: soon, onResync: soon });
  return () => {
    dead = true;
    if (timer) clearTimeout(timer);
    clearInterval(poll);
    unsub();
  };
}

/** What the board needs about a linked notice — the limited feed every dispatcher gets. */
export type BoardNotice = Pick<PaymentNotice,
  'id' | 'cardId' | 'receivedAt' | 'senderName' | 'amount' | 'memo' | 'interacRef' | 'flags' | 'status' | 'archivedAt'>;

export function boardNoticeFromRow(r: any): BoardNotice {
  return {
    id: Number(r.id),
    cardId: r.card_id ?? null,
    receivedAt: Date.parse(r.received_at),
    senderName: r.sender_name ?? null,
    amount: num(r.amount),
    memo: r.memo ?? null,
    interacRef: r.interac_ref ?? null,
    flags: (r.flags || []) as NoticeFlag[],
    status: r.status,
    archivedAt: null,
  };
}

/** The board's notices: on every notice change this tab can see, and once a minute (a dispatcher without
 *  Payments access gets no realtime for the table, so the minute poll is theirs). */
export function subscribeBoardNotices(onRows: (rows: BoardNotice[]) => void, onError?: (e: Error) => void): () => void {
  let dead = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const load = () => {
    Promise.resolve(supabase().rpc('etransfer_board_notices')).then(({ data, error }: any) => {
      if (dead) return;
      if (error) onError?.(error);
      else onRows((data || []).map(boardNoticeFromRow));
    }, (e) => { if (!dead) onError?.(e); });
  };
  const soon = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(load, 300);
  };
  load();
  const poll = setInterval(load, REFRESH_MS);
  const unsub = subscribeTableHub('etransfer_notices', { onChange: soon, onResync: soon });
  return () => {
    dead = true;
    if (timer) clearTimeout(timer);
    clearInterval(poll);
    unsub();
  };
}

const refKey = (s: string | null | undefined) => (s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

/** Payment Received and every column after it (0118; pages/PaymentApproval.tsx STAGE_RANK >= completed). */
export const PAID_STAGES: readonly string[] = [
  'completed', 'packing', 'handed_to_driver', 'returning_to_store', 'print_label', 'shipping', 'delivered',
];

type CardPay = { paidAt?: number | null; stage?: string | null; eTransferRef?: string | null };

/** Paid = approved (paidAt) or at / past Payment Received — cards approved before their notice was read
 *  count too (0118, 2026-10-06). The same rule as etransfer_notices_board. */
export function isCardPaid(card: CardPay): boolean {
  return !!card.paidAt || PAID_STAGES.includes(card.stage || '');
}

/** Was the card approved with this notice's Interac reference? Decided from the live card, so the board
 *  shows ACCEPTED the moment Approve lands — no wait for the notices list. */
export function isAcceptedFor(n: Pick<BoardNotice, 'interacRef'>, card: CardPay): boolean {
  return isCardPaid(card) && !!n.interacRef && refKey(card.eTransferRef) === refKey(n.interacRef);
}

/** Payment notices per card, newest first (ordinary mail and Interac info notices left out). */
export function noticesByCard<T extends BoardNotice>(rows: T[]): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const n of rows) {
    if (!n.cardId || n.archivedAt || n.status === 'not_payment' || n.status === 'info') continue;
    const list = out.get(n.cardId) || [];
    list.push(n);
    out.set(n.cardId, list);
  }
  for (const list of out.values()) list.sort((a, b) => b.receivedAt - a.receivedAt);
  return out;
}

/** The notice the Approve dialog should pre-select: the only live (not cancelled) one, if exactly one. */
export function noticeToPreselect<T extends BoardNotice>(list: T[] | undefined): T | null {
  const live = (list || []).filter((n) => n.status !== 'cancelled' && n.interacRef);
  return live.length === 1 ? live[0] : null;
}

export async function linkNotice(noticeId: number, cardId: string): Promise<void> {
  const { error } = await supabase().rpc('etransfer_notice_link', { p_notice: noticeId, p_card: cardId });
  if (error) throw error;
}

export async function unlinkNotice(noticeId: number): Promise<void> {
  const { error } = await supabase().rpc('etransfer_notice_unlink', { p_notice: noticeId });
  if (error) throw error;
}

export async function archiveNotice(noticeId: number): Promise<void> {
  const { error } = await supabase().rpc('etransfer_notice_archive', { p_notice: noticeId });
  if (error) throw error;
}

export async function unarchiveNotice(noticeId: number): Promise<void> {
  const { error } = await supabase().rpc('etransfer_notice_unarchive', { p_notice: noticeId });
  if (error) throw error;
}

export interface CardSuggestion {
  id: string;
  orderNumber: string;
  customerName: string;
  total: number;
  stage: string;
  nameMatch: boolean;
  amountMatch: boolean;
}

/** In-progress, unpaid cards whose customer name and/or total match the notice (both first) — for a
 *  notice whose message names no order. */
export async function fetchSuggestions(noticeId: number): Promise<CardSuggestion[]> {
  const { data, error } = await supabase().rpc('etransfer_notice_suggestions', { p_notice: noticeId });
  if (error) throw error;
  return (data || []).map((r: any) => ({
    id: r.card_id,
    orderNumber: r.order_number || '',
    customerName: r.customer_name || '',
    total: Number(r.total) || 0,
    stage: r.stage,
    nameMatch: !!r.name_match,
    amountMatch: !!r.amount_match,
  }));
}

export interface CardHit {
  id: string;
  orderNumber: string;
  customerName: string;
  total: number;
  stage: string;
}

/** Cards a dispatcher can link a notice to: by order number (any punctuation) or customer name. */
export async function searchCards(q: string): Promise<CardHit[]> {
  const text = q.trim();
  if (text.length < 2) return [];
  const key = orderKeyOf(text);
  const safe = text.replace(/[%,()]/g, ' ');
  const filters = [`customer_name.ilike.%${safe}%`];
  if (key && key.length >= 3) filters.push(`order_key.ilike.%${key}%`);
  const { data, error } = await supabase()
    .from('payment_board')
    .select('id,order_number,customer_name,total,stage')
    .or(filters.join(','))
    .order('placed_at', { ascending: false })
    .limit(10);
  if (error) throw error;
  return (data || []).map((r: any) => ({
    id: r.id,
    orderNumber: r.order_number || '',
    customerName: r.customer_name || '',
    total: Number(r.total) || 0,
    stage: r.stage,
  }));
}

// ── Access ───────────────────────────────────────────────────────────────────

const accessCache = new Map<string, Promise<boolean>>();

/** May this user open Payments? A super admin always; anyone else only when granted (asked once per session). */
export function hasPaymentsAccess(user: Pick<User, 'id' | 'role'> | null | undefined): Promise<boolean> {
  if (!user) return Promise.resolve(false);
  if (isSuperTier(user.role)) return Promise.resolve(true);
  let p = accessCache.get(user.id);
  if (!p) {
    p = Promise.resolve(supabase().rpc('has_payments_access')).then(({ data, error }: any) => !error && data === true, () => false);
    accessCache.set(user.id, p);
  }
  return p;
}
/** React: whether the signed-in user may open Payments (false until known). */
export function usePaymentsAccess(user: Pick<User, 'id' | 'role'> | null | undefined): boolean {
  const [ok, setOk] = useState(isSuperTier(user?.role));
  useEffect(() => {
    let live = true;
    hasPaymentsAccess(user).then((v) => live && setOk(v));
    return () => { live = false; };
  }, [user?.id, user?.role]);
  return ok;
}

/** Test seam. */
export function _resetPaymentsAccessCache(): void { accessCache.clear(); }

export interface PaymentsAccessRow {
  userId: string;
  grantedByName: string | null;
  grantedAt: number;
}

export async function fetchPaymentsAccess(): Promise<PaymentsAccessRow[]> {
  const { data, error } = await supabase()
    .from('payments_access')
    .select('user_id,granted_by_name,granted_at')
    .order('granted_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((r: any) => ({ userId: r.user_id, grantedByName: r.granted_by_name ?? null, grantedAt: Date.parse(r.granted_at) }));
}

export async function grantPaymentsAccess(userId: string): Promise<void> {
  const { error } = await supabase().rpc('payments_access_grant', { p_user: userId });
  if (error) throw error;
  accessCache.delete(userId);
}

export async function revokePaymentsAccess(userId: string): Promise<void> {
  const { error } = await supabase().rpc('payments_access_revoke', { p_user: userId });
  if (error) throw error;
  accessCache.delete(userId);
}

// ── Payments log ─────────────────────────────────────────────────────────────

export type PaymentEventKind = 'linked' | 'unlinked' | 'archived' | 'unarchived' | 'access_granted' | 'access_revoked';

export interface PaymentEvent {
  id: number;
  at: number;
  actorName: string | null;
  kind: PaymentEventKind;
  noticeId: number | null;
  cardId: string | null;
  subjectUserId: string | null;
  detail: Record<string, any>;
}

const eventFromRow = (r: any): PaymentEvent => ({
  id: Number(r.id),
  at: Date.parse(r.created_at),
  actorName: r.actor_name ?? null,
  kind: r.kind,
  noticeId: r.notice_id ?? null,
  cardId: r.card_id ?? null,
  subjectUserId: r.subject_user_id ?? null,
  detail: r.detail || {},
});

const EVENT_COLUMNS = 'id,created_at,actor_name,kind,notice_id,card_id,subject_user_id,detail';

/** What was done to one notice, oldest first. */
export async function fetchNoticeEvents(noticeId: number): Promise<PaymentEvent[]> {
  const { data, error } = await supabase().from('payment_events').select(EVENT_COLUMNS)
    .eq('notice_id', noticeId).order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map(eventFromRow);
}

/** The latest access grants and removals (super admin only — the database hides them from anyone else). */
export async function fetchAccessEvents(limit = 10): Promise<PaymentEvent[]> {
  const { data, error } = await supabase().from('payment_events').select(EVENT_COLUMNS)
    .in('kind', ['access_granted', 'access_revoked']).order('created_at', { ascending: false }).limit(limit);
  if (error) throw error;
  return (data || []).map(eventFromRow);
}

export const EVENT_TEXT: Record<PaymentEventKind, string> = {
  linked: 'Linked',
  unlinked: 'Unlinked',
  archived: 'Archived',
  unarchived: 'Unarchived',
  access_granted: 'Gave access',
  access_revoked: 'Removed access',
};
